import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/apiService';
import { isQaEnvironment } from '../utils/subdomain';
import './SyncAmbientes.css';

/**
 * Sincronizador de Ambientes — passo 1: direção e escopo.
 *
 * Só existe em QA (`isQaEnvironment()`), e só para admin irrestrito da loja Aliado Admin
 * (`user.can_sync`, vindo do /auth/me). As duas travas aqui são cosméticas: quem recusa
 * de verdade é a API (`_require_sync_admin` + `APP_ENV`). Esconder botão não protege rota.
 *
 * Desenho: aliado-recorder-api/docs/sincronizador-ambientes-plano.md
 */

// As direções são fixas por classe de dado, e a regra é revalidada NO DESTINO pela API —
// quem chama pode mentir. Aqui a lista existe só para a tela não oferecer o impossível.
const DIRECOES = [
    { id: 'prod-qa', origem: 'prod', destino: 'qa', classe: 'atendimentos',
      titulo: 'Atendimentos', de: 'Produção', para: 'QA', risco: 'baixo',
      nota: 'Traz atendimentos reais para QA. O destino não é produção.' },
    { id: 'prod-hmg', origem: 'prod', destino: 'hmg', classe: 'atendimentos',
      titulo: 'Atendimentos', de: 'Produção', para: 'Homologação', risco: 'baixo',
      nota: 'Traz atendimentos reais para homologação. O destino não é produção.' },
    { id: 'qa-hmg', origem: 'qa', destino: 'hmg', classe: 'config',
      titulo: 'Configurações', de: 'QA', para: 'Homologação', risco: 'medio',
      nota: 'Leva configuração para homologação — que se comporta como produção, mas não é.' },
    { id: 'prod-hmg-config', origem: 'prod', destino: 'hmg', classe: 'config',
      titulo: 'Configurações', de: 'Produção', para: 'Homologação', risco: 'medio',
      nota: 'Alinha homologação com produção — que é o papel dela. Use esta, e não a de '
          + 'QA, quando a intenção for "deixar HMG parecido com produção": QA acumula '
          + 'dado de teste e levaria isso junto.' },
    { id: 'prod-qa-config', origem: 'prod', destino: 'qa', classe: 'config',
      titulo: 'Configurações', de: 'Produção', para: 'QA', risco: 'baixo',
      nota: 'Traz QA de volta para a linha de base. QA acumula dado de teste por '
          + 'construção, então é o ambiente que mais se afasta de produção.' },
    { id: 'hmg-qa-config', origem: 'hmg', destino: 'qa', classe: 'config',
      titulo: 'Configurações', de: 'Homologação', para: 'QA', risco: 'baixo',
      nota: 'Caminho curto para o mesmo efeito, já que homologação é cópia de produção. '
          + 'Serve para trazer para QA uma configuração já homologada.' },
    { id: 'qa-prod', origem: 'qa', destino: 'prod', classe: 'config',
      titulo: 'Configurações', de: 'QA', para: 'Produção', risco: 'alto',
      nota: 'Escreve em PRODUÇÃO. Exige conferência item a item e tem desfazer.' }
];

const NOME_AMBIENTE = { prod: 'Produção', qa: 'QA', hmg: 'Homologação' };

export default function SyncAmbientes({ user }) {
    const ehQa = isQaEnvironment();

    const [ambientes, setAmbientes] = useState(null);
    const [empresas, setEmpresas] = useState([]);
    const [direcao, setDirecao] = useState(null);
    const [subdomain, setSubdomain] = useState('');
    const [erro, setErro] = useState(null);
    const [carregando, setCarregando] = useState(true);

    useEffect(() => {
        if (!ehQa || !user?.can_sync) { setCarregando(false); return; }
        let vivo = true;
        (async () => {
            try {
                const [envs, comps] = await Promise.all([
                    apiService.syncEnvs(),
                    apiService.promptEditorGetCompanies()
                ]);
                if (!vivo) return;
                setAmbientes(envs.ambientes || []);
                const lista = Array.isArray(comps) ? comps : (comps?.companies || []);
                setEmpresas(lista.filter((c) => c.subdomain && c.subdomain !== 'admin'));
            } catch (e) {
                if (vivo) setErro(e?.response?.data?.error || e.message);
            } finally {
                if (vivo) setCarregando(false);
            }
        })();
        return () => { vivo = false; };
    }, [ehQa, user]);

    const configurado = useMemo(() => {
        const m = {};
        (ambientes || []).forEach((a) => { m[a.env] = a.configurado; });
        return m;
    }, [ambientes]);

    if (!ehQa) {
        return (
            <div className="sync-wrap">
                <div className="sync-bloqueio">
                    <h1>Sincronizador de Ambientes</h1>
                    <p>Esta ferramenta existe <strong>somente em QA</strong>. Acesse por
                       <code> admin-qa.alia.do</code>.</p>
                </div>
            </div>
        );
    }

    if (!user?.can_sync) {
        return (
            <div className="sync-wrap">
                <div className="sync-bloqueio">
                    <h1>Sincronizador de Ambientes</h1>
                    <p>Acesso restrito a administradores da <strong>Aliado Admin</strong> sem
                       limitação por empresa.</p>
                </div>
            </div>
        );
    }

    const podeSeguir = direcao && subdomain
        && configurado[direcao.origem] && configurado[direcao.destino];

    function seguir() {
        if (!podeSeguir) return;
        window.location.href = `/sync/${direcao.id}/${encodeURIComponent(subdomain)}`;
    }

    return (
        <div className="sync-wrap">
            <header className="sync-head">
                <h1>🔀 Sincronizador de Ambientes</h1>
                <p className="sync-sub">
                    Compare dois ambientes e escolha o que levar. Nada é gravado sem você
                    conferir a diferença e aprovar.
                </p>
            </header>

            {erro && <div className="sync-erro">⚠️ {erro}</div>}
            {carregando && <div className="sync-info">Carregando…</div>}

            {!carregando && (
                <>
                    <section className="sync-secao">
                        <h2>1. O que você quer levar, e para onde</h2>
                        <div className="sync-direcoes">
                            {DIRECOES.map((d) => {
                                const indisponivel = !configurado[d.origem] || !configurado[d.destino];
                                const faltando = [d.origem, d.destino]
                                    .filter((e) => !configurado[e])
                                    .map((e) => NOME_AMBIENTE[e]);
                                return (
                                    <button
                                        key={d.id}
                                        type="button"
                                        className={`sync-card risco-${d.risco}`
                                            + (direcao?.id === d.id ? ' selecionado' : '')
                                            + (indisponivel ? ' indisponivel' : '')}
                                        disabled={indisponivel}
                                        title={indisponivel
                                            ? `Credencial não configurada no servidor: ${faltando.join(', ')}`
                                            : d.nota}
                                        onClick={() => setDirecao(d)}
                                    >
                                        <span className="sync-card-tipo">{d.titulo}</span>
                                        <span className="sync-card-rota">
                                            {d.de} <span className="sync-seta">→</span> {d.para}
                                        </span>
                                        <span className="sync-card-nota">{d.nota}</span>
                                        {indisponivel && (
                                            <span className="sync-card-alerta">
                                                Indisponível — falta credencial de {faltando.join(' e ')}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section className="sync-secao">
                        <h2>2. Qual cliente</h2>
                        <p className="sync-nota">
                            Um cliente por vez, de propósito: o diff fica legível e evita erro em massa.
                        </p>
                        <select
                            className="sync-select"
                            value={subdomain}
                            onChange={(e) => setSubdomain(e.target.value)}
                        >
                            <option value="">Selecione o cliente…</option>
                            {empresas.map((c) => (
                                <option key={c.subdomain} value={c.subdomain}>
                                    {c.name} ({c.subdomain})
                                </option>
                            ))}
                        </select>
                    </section>

                    <section className="sync-secao">
                        <button
                            type="button"
                            className="sync-botao-principal"
                            disabled={!podeSeguir}
                            onClick={seguir}
                            title={podeSeguir ? 'Comparar os dois ambientes'
                                              : 'Escolha a direção e o cliente'}
                        >
                            Comparar diferenças →
                        </button>
                        {direcao?.risco === 'alto' && (
                            <p className="sync-aviso-alto">
                                Essa direção escreve em <strong>produção</strong>. A comparação a
                                seguir é só leitura — nada é gravado neste passo.
                            </p>
                        )}
                    </section>
                </>
            )}
        </div>
    );
}
